import React, { useState } from "react";
import { trpc } from "../../lib/trpc.js";
import {
  X,
  Loader2,
  Palette,
  DollarSign,
  ImagePlus,
  FileText,
  MapPin,
  FolderOpen,
  Plus,
  Trash2,
} from "lucide-react";
import {
  NICHE_OPTIONS,
  GEO_OPTIONS,
  PROJECT_OPTIONS,
  GEO_CURRENCY_MAP,
} from "@ymonetize/shared";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (taskId: string) => void;
}

export function CreateCreativesModal({ open, onClose, onSuccess }: Props) {
  const [project, setProject] = useState("");
  const [niche, setNiche] = useState("");
  const [customNiche, setCustomNiche] = useState("");
  const [geo, setGeo] = useState("MX");
  const [currency, setCurrency] = useState("MXN");
  const [dailyBudget, setDailyBudget] = useState(200);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [newImageUrl, setNewImageUrl] = useState("");
  const [creativeDescription, setCreativeDescription] = useState("");
  const [referenceCopys, setReferenceCopys] = useState("");
  const [notes, setNotes] = useState("");

  const createCreatives = trpc.brain.createCreatives.useMutation({
    onSuccess: (task) => {
      onSuccess(task.id);
      onClose();
    },
  });

  function handleGeoChange(newGeo: string) {
    setGeo(newGeo);
    setCurrency(GEO_CURRENCY_MAP[newGeo] || "USD");
  }

  function addImageUrl() {
    if (newImageUrl && imageUrls.length < 10) {
      setImageUrls([...imageUrls, newImageUrl]);
      setNewImageUrl("");
    }
  }

  function removeImageUrl(index: number) {
    setImageUrls(imageUrls.filter((_, i) => i !== index));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const finalNiche = niche === "custom" ? customNiche : niche;
    if (!finalNiche || !project) return;

    createCreatives.mutate({
      project,
      niche: finalNiche,
      geo,
      imageUrls: imageUrls.length > 0 ? imageUrls : undefined,
      creativeDescription: creativeDescription || undefined,
      referenceCopys: referenceCopys || undefined,
      dailyBudget,
      currency,
      notes: notes || undefined,
    });
  }

  if (!open) return null;

  const isValid = project.length > 0 && (niche === "custom" ? customNiche.length > 0 : niche.length > 0) && dailyBudget > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-card border border-border rounded-2xl shadow-2xl animate-scale-in max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
              <Palette className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Criar Criativos</h2>
              <p className="text-xs text-muted-foreground">Gerar criativos + estratégia de ads</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Project */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
              <FolderOpen className="w-3.5 h-3.5 text-orange-500" />
              Projeto no Publicador
            </label>
            <select
              value={project}
              onChange={(e) => setProject(e.target.value)}
              className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
            >
              <option value="">Selecione um projeto...</option>
              {PROJECT_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* Niche + Geo */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Nicho</label>
              <select
                value={niche}
                onChange={(e) => setNiche(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
              >
                <option value="">Selecione...</option>
                {NICHE_OPTIONS.map((n) => (
                  <option key={n.value} value={n.value}>{n.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-orange-500" />
                País
              </label>
              <select
                value={geo}
                onChange={(e) => handleGeoChange(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
              >
                {GEO_OPTIONS.map((g) => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>
            </div>
          </div>

          {niche === "custom" && (
            <input
              type="text"
              value={customNiche}
              onChange={(e) => setCustomNiche(e.target.value)}
              placeholder="Descreva o nicho personalizado..."
              className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
            />
          )}

          {/* Budget */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5 text-orange-500" />
              Budget Diário
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={dailyBudget}
                onChange={(e) => setDailyBudget(Number(e.target.value))}
                min={1}
                max={100000}
                className="flex-1 px-3 py-2.5 bg-background border border-input rounded-xl text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
              />
              <span className="self-center text-sm text-muted-foreground font-mono">{currency}</span>
            </div>
          </div>

          {/* Image URLs */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
              <ImagePlus className="w-3.5 h-3.5 text-orange-500" />
              Imagens de Referência
              <span className="text-[10px] text-muted-foreground">(opcional, máx 10)</span>
            </label>
            {imageUrls.length > 0 && (
              <div className="space-y-1.5 mb-2">
                {imageUrls.map((url, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs bg-background border border-input rounded-lg px-3 py-2">
                    <span className="flex-1 truncate text-muted-foreground">{url}</span>
                    <button type="button" onClick={() => removeImageUrl(i)} className="text-destructive hover:text-destructive/80">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {imageUrls.length < 10 && (
              <div className="flex gap-2">
                <input
                  type="url"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  placeholder="https://exemplo.com/imagem.jpg"
                  className="flex-1 px-3 py-2 bg-background border border-input rounded-xl text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addImageUrl();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={addImageUrl}
                  disabled={!newImageUrl}
                  className="px-3 py-2 bg-secondary text-secondary-foreground rounded-xl text-sm hover:opacity-80 disabled:opacity-40"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Creative Description */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
              <Palette className="w-3.5 h-3.5 text-orange-500" />
              Estilo Desejado
              <span className="text-[10px] text-muted-foreground">(opcional)</span>
            </label>
            <textarea
              value={creativeDescription}
              onChange={(e) => setCreativeDescription(e.target.value)}
              placeholder="Ex: Estilo lifestyle, cores quentes, sem texto pesado..."
              rows={2}
              className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring/50"
            />
          </div>

          {/* Reference Copys */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-orange-500" />
              Copys de Referência
              <span className="text-[10px] text-muted-foreground">(opcional)</span>
            </label>
            <textarea
              value={referenceCopys}
              onChange={(e) => setReferenceCopys(e.target.value)}
              placeholder="Cole aqui copys que já funcionaram para inspirar o Brain..."
              rows={3}
              className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring/50"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-orange-500" />
              Observações
              <span className="text-[10px] text-muted-foreground">(opcional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Instruções adicionais..."
              rows={2}
              className="w-full px-3 py-2.5 bg-background border border-input rounded-xl text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring/50"
            />
          </div>

          {/* Error */}
          {createCreatives.error && (
            <p className="text-xs text-destructive">{createCreatives.error.message}</p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={!isValid || createCreatives.isPending}
            className="w-full py-3 bg-gradient-to-r from-orange-600 to-primary text-white rounded-xl text-sm font-semibold hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-40 flex items-center justify-center gap-2 shadow-lg shadow-orange-500/20"
          >
            {createCreatives.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Criando criativos...
              </>
            ) : (
              <>
                <Palette className="w-4 h-4" />
                Criar Criativos + Estratégia
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
